import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
  FirestoreDataConverter
} from "firebase/firestore";
import { firestore } from "@/base";
import { getCurrentUser } from "@/services/auth";
import type { TRelatorioGerado, TRelatorioGeradoInput } from "../types";

/**
 * Converter para documentos de relatórios gerados
 */
const reportConverter: FirestoreDataConverter<TRelatorioGerado> = {
  toFirestore(data) {
    const dataToSave = { ...data };
    delete (dataToSave as Partial<TRelatorioGerado>).id;
    return dataToSave;
  },
  fromFirestore(snap) {
    const data = snap.data();
    return {
      ...data,
      id: snap.id,
      createdAt: data.createdAt
    } as TRelatorioGerado;
  }
};

/**
 * Get reports collection reference (root-level collection)
 */
function getReportsCollectionReference() {
  return collection(firestore, "relatorios_gerados").withConverter(reportConverter);
}

/**
 * Get report document reference
 * @param reportId
 */
function getReportDocumentReference(reportId: string) {
  const collectionReference = getReportsCollectionReference();
  return doc(collectionReference, reportId);
}

/**
 * Save a report to history
 * @param data
 */
export async function saveReportHistory(
  data: Omit<TRelatorioGeradoInput, "geradoPor" | "createdAt">
): Promise<TRelatorioGerado> {
  const currentUserId = getCurrentUser()?.uid;
  if (!currentUserId) {
    throw new Error("User not authenticated");
  }

  const collectionReference = getReportsCollectionReference();

  const reportData = {
    ...data,
    geradoPor: currentUserId,
    createdAt: serverTimestamp()
  };

  const documentReference = await addDoc(collectionReference, reportData as unknown as TRelatorioGerado);
  const newDocument = await getDoc(documentReference);

  return newDocument.data()!;
}

/**
 * Get report history for an empresa
 * @param empresaId
 * @param maxResults
 */
export async function getReportHistoryByEmpresa(empresaId: string, maxResults = 20): Promise<TRelatorioGerado[]> {
  const collectionReference = getReportsCollectionReference();
  const q = query(
    collectionReference,
    where("idEmpresa", "==", empresaId),
    orderBy("createdAt", "desc"),
    limit(maxResults)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((document_) => document_.data());
}

/**
 * Get report history for a questionario
 * @param questionarioId
 * @param maxResults
 */
export async function getReportHistoryByQuestionario(
  questionarioId: string,
  maxResults = 20
): Promise<TRelatorioGerado[]> {
  const collectionReference = getReportsCollectionReference();
  const q = query(
    collectionReference,
    where("idQuestionario", "==", questionarioId),
    orderBy("createdAt", "desc"),
    limit(maxResults)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((document_) => document_.data());
}

/**
 * Get report history for current user
 * @param maxResults
 */
export async function getReportHistoryByUser(maxResults = 50): Promise<TRelatorioGerado[]> {
  const currentUserId = getCurrentUser()?.uid;
  if (!currentUserId) {
    throw new Error("User not authenticated");
  }

  const collectionReference = getReportsCollectionReference();
  const q = query(
    collectionReference,
    where("geradoPor", "==", currentUserId),
    orderBy("createdAt", "desc"),
    limit(maxResults)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((document_) => document_.data());
}

/**
 * Get a report by ID
 * @param reportId
 */
export async function getReportById(reportId: string): Promise<TRelatorioGerado | null> {
  const documentReference = getReportDocumentReference(reportId);
  const snapshot = await getDoc(documentReference);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data();
}
